import { useState, useEffect, useCallback } from 'react';
import { getCategories, getSubCategoriesByParentCategory } from '../pages/video';

/**
 * Manages dependent Category → Subcategory selection.
 * Changing category always clears subcategory and reloads subcategory options.
 */
export function useCategorySubcategory({ autoLoadCategories = true } = {}) {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState(null);
  const [subCategory, setSubCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubCategories, setLoadingSubCategories] = useState(false);
  const [subCategoryFieldKey, setSubCategoryFieldKey] = useState(0);

  const fetchSubCategories = useCallback(async (categoryId) => {
    if (!categoryId) {
      setSubCategories([]);
      return [];
    }

    setLoadingSubCategories(true);
    try {
      const response = await getSubCategoriesByParentCategory(categoryId);
      const items = response.data?.data || [];
      setSubCategories(items);
      return items;
    } catch (error) {
      setSubCategories([]);
      return [];
    } finally {
      setLoadingSubCategories(false);
    }
  }, []);

  const loadCategories = useCallback(async () => {
    setLoadingCategories(true);
    try {
      const response = await getCategories();
      const items = response.data?.data || [];
      setCategories(items);
      return items;
    } catch (error) {
      setCategories([]);
      return [];
    } finally {
      setLoadingCategories(false);
    }
  }, []);

  useEffect(() => {
    if (autoLoadCategories) {
      loadCategories();
    }
  }, [autoLoadCategories, loadCategories]);

  const handleCategoryChange = useCallback(
    (newCategory) => {
      setCategory(newCategory || null);
      setSubCategory(null);
      setSubCategories([]);
      setSubCategoryFieldKey((key) => key + 1);

      if (newCategory?.id) {
        fetchSubCategories(newCategory.id);
      }
    },
    [fetchSubCategories]
  );

  const handleSubCategoryChange = useCallback((newSubCategory) => {
    setSubCategory(newSubCategory || null);
  }, []);

  /** Restore saved category/subcategory names (e.g. video edit initial load). */
  const initializeFromNames = useCallback(
    async (categoryName, subCategoryName) => {
      if (!categoryName) return;

      let categoryList = categories;
      if (categoryList.length === 0) {
        categoryList = await loadCategories();
      }

      const matchedCategory = categoryList.find((item) => item.name === categoryName);
      if (!matchedCategory) return;

      setCategory(matchedCategory);
      const subs = await fetchSubCategories(matchedCategory.id);
      const matchedSubCategory = subCategoryName
        ? subs.find((item) => item.name === subCategoryName) || null
        : null;
      setSubCategory(matchedSubCategory);
      setSubCategoryFieldKey((key) => key + 1);
    },
    [categories, loadCategories, fetchSubCategories]
  );

  const reset = useCallback(() => {
    setCategory(null);
    setSubCategory(null);
    setSubCategories([]);
    setSubCategoryFieldKey((key) => key + 1);
  }, []);

  const isSubCategoryValid =
    !category ||
    (!!subCategory && subCategories.some((item) => item.id === subCategory.id));

  return {
    categories,
    category,
    subCategory,
    subCategories,
    loadingCategories,
    loadingSubCategories,
    subCategoryFieldKey,
    categoryName: category?.name ?? '',
    subCategoryName: subCategory?.name ?? '',
    categoryId: category?.id ?? null,
    isSubCategoryValid,
    handleCategoryChange,
    handleSubCategoryChange,
    initializeFromNames,
    loadCategories,
    fetchSubCategories,
    reset,
  };
}
